import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatErr } from "@/lib/api";
import { inputCls, labelCls, btnPrimary, btnAccent, btnOutline } from "@/components/Modal";
import { TableCard, theadCls, thCls, tdCls, rowCls, EmptyRow } from "@/components/Table";
import {
  CloudUpload, RefreshCw, RotateCcw, Trash2, Link2, Unlink, CheckCircle2,
  AlertTriangle, ChevronDown, ShieldCheck, Copy, HardDrive,
} from "lucide-react";
import { toast } from "sonner";

function fmtBytes(n) {
  n = Number(n) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}
function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

// Smoothly animates a number from 0 → target (easeOutCubic) whenever target changes.
function useCountUp(target, duration = 1200) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (target == null || isNaN(target)) return;
    let raf, start = null;
    const step = (t) => {
      if (start === null) start = t;
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(target * eased);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
}

// Animated Google Drive storage gauge: a ring that sweeps to the used %, with
// counting-up numbers and animated Used/Free bars.
function StorageGauge({ quota }) {
  const unlimited = quota.limit == null;
  const pct = unlimited ? 0 : Math.min(100, (quota.usage / quota.limit) * 100);

  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const pctAnim = useCountUp(pct);
  const usedAnim = useCountUp(quota.usage);
  const freeAnim = useCountUp(unlimited ? 0 : quota.free);

  const R = 56, STROKE = 12, C = 2 * Math.PI * R;
  const offset = mounted ? C * (1 - pct / 100) : C;
  const tone = pct < 70 ? "#2f7d32" : pct < 90 ? "#e0a106" : "#dc2626";
  const toneSoft = pct < 70 ? "#7cb342" : pct < 90 ? "#f6c453" : "#f0736b";

  return (
    <div className="card-soft p-6">
      <div className="flex items-center gap-2 mb-1">
        <HardDrive size={18} className="text-agri-forest" />
        <h3 className="font-heading text-xl">Google Drive Storage</h3>
      </div>
      <p className="text-xs text-agri-ink-2 mb-5">Live space on the connected Google account.</p>

      <div className="flex flex-col sm:flex-row items-center gap-8">
        {/* Donut */}
        <div className="relative shrink-0" style={{ width: 160, height: 160 }}>
          <svg width="160" height="160" viewBox="0 0 160 160" className="-rotate-90">
            <defs>
              <linearGradient id="gaugeGrad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor={toneSoft} />
                <stop offset="100%" stopColor={tone} />
              </linearGradient>
            </defs>
            <circle cx="80" cy="80" r={R} fill="none" stroke="rgba(28,28,30,0.08)" strokeWidth={STROKE} />
            <circle
              cx="80" cy="80" r={R} fill="none" stroke={unlimited ? "#2f7d32" : "url(#gaugeGrad)"}
              strokeWidth={STROKE} strokeLinecap="round"
              strokeDasharray={C} strokeDashoffset={unlimited ? 0 : offset}
              style={{ transition: "stroke-dashoffset 1.3s cubic-bezier(.22,1,.36,1)" }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            {unlimited ? (
              <span className="text-2xl font-heading text-agri-forest">∞</span>
            ) : (
              <>
                <span className="text-3xl font-heading tabular-nums" style={{ color: tone }}>
                  {pctAnim.toFixed(0)}<span className="text-lg">%</span>
                </span>
                <span className="text-[11px] uppercase tracking-wider text-agri-ink-2">used</span>
              </>
            )}
          </div>
        </div>

        {/* Stats + bars */}
        <div className="flex-1 w-full space-y-4">
          <div className="grid grid-cols-3 gap-3 text-center">
            <div>
              <div className="label-cap">Total</div>
              <div className="mt-0.5 font-heading text-lg">{unlimited ? "∞" : fmtBytes(quota.limit)}</div>
            </div>
            <div>
              <div className="label-cap">Used</div>
              <div className="mt-0.5 font-heading text-lg tabular-nums" style={{ color: tone }}>{fmtBytes(usedAnim)}</div>
            </div>
            <div>
              <div className="label-cap">Free</div>
              <div className="mt-0.5 font-heading text-lg tabular-nums text-agri-forest">
                {unlimited ? "∞" : fmtBytes(freeAnim)}
              </div>
            </div>
          </div>

          {!unlimited && (
            <div className="space-y-1.5">
              <div className="h-3 rounded-full bg-agri-ink/[0.07] overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: mounted ? `${pct}%` : "0%",
                    background: `linear-gradient(90deg, ${toneSoft}, ${tone})`,
                    transition: "width 1.3s cubic-bezier(.22,1,.36,1)",
                  }}
                />
              </div>
              <div className="flex justify-between text-[11px] text-agri-ink-2">
                <span>{fmtBytes(quota.usage)} used</span>
                <span>{fmtBytes(quota.free)} free of {fmtBytes(quota.limit)}</span>
              </div>
            </div>
          )}
          <p className="text-[11px] text-agri-ink-2">
            Backups are tiny (a shop’s data is a few MB), so a free 15 GB account holds years of them.
          </p>
        </div>
      </div>
    </div>
  );
}

const ERR_MSG = {
  invalid_state: "Connection link expired. Please try connecting again.",
  not_configured: "Save your Google Client ID and Secret first.",
  no_refresh_token: "Google didn't return a refresh token. Remove the app's access in your Google Account and reconnect.",
  connect_failed: "Could not connect to Google Drive. Check your Client ID/Secret and redirect URI.",
  access_denied: "You cancelled the Google sign-in.",
};

export default function Backups() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [creds, setCreds] = useState({ client_id: "", client_secret: "" });
  const [guideOpen, setGuideOpen] = useState(false);

  const { data: status, isLoading } = useQuery({
    queryKey: ["backup-status"],
    queryFn: () => api.get("/backup/status").then((r) => r.data),
  });
  const connected = status?.connected;

  const { data: backups = [], isFetching: listLoading } = useQuery({
    queryKey: ["backup-list"],
    queryFn: () => api.get("/backup/list").then((r) => r.data),
    enabled: !!connected,
  });

  const { data: quota } = useQuery({
    queryKey: ["backup-quota"],
    queryFn: () => api.get("/backup/quota").then((r) => r.data),
    enabled: !!connected,
    staleTime: 30_000,
  });

  // Handle the redirect back from Google
  useEffect(() => {
    if (params.get("connected")) {
      toast.success("Google Drive connected");
      qc.invalidateQueries({ queryKey: ["backup-status"] });
      qc.invalidateQueries({ queryKey: ["backup-list"] });
      qc.invalidateQueries({ queryKey: ["backup-quota"] });
      params.delete("connected"); setParams(params, { replace: true });
    }
    const err = params.get("error");
    if (err) {
      toast.error(ERR_MSG[err] || `Google connection failed (${err})`);
      params.delete("error"); setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const saveCfg = useMutation({
    mutationFn: () => api.post("/backup/config", creds),
    onSuccess: () => { toast.success("Credentials saved"); qc.invalidateQueries({ queryKey: ["backup-status"] }); },
    onError: (e) => toast.error(formatErr(e.response?.data?.detail)),
  });

  const connect = useMutation({
    mutationFn: () => api.get("/backup/auth-url").then((r) => r.data),
    onSuccess: (d) => { window.location.href = d.url; },
    onError: (e) => toast.error(formatErr(e.response?.data?.detail)),
  });

  const disconnect = useMutation({
    mutationFn: () => api.post("/backup/disconnect"),
    onSuccess: () => { toast.success("Disconnected"); qc.invalidateQueries({ queryKey: ["backup-status"] }); },
    onError: (e) => toast.error(formatErr(e.response?.data?.detail)),
  });

  const runBackup = useMutation({
    mutationFn: () => api.post("/backup/run"),
    onSuccess: () => {
      toast.success("Backup uploaded to Google Drive");
      qc.invalidateQueries({ queryKey: ["backup-status"] });
      qc.invalidateQueries({ queryKey: ["backup-list"] });
      qc.invalidateQueries({ queryKey: ["backup-quota"] });
    },
    onError: (e) => toast.error(formatErr(e.response?.data?.detail)),
  });

  const restore = useMutation({
    mutationFn: (file_id) => api.post("/backup/restore", { file_id }),
    onSuccess: () => { toast.success("Database restored"); qc.invalidateQueries(); },
    onError: (e) => toast.error(formatErr(e.response?.data?.detail)),
  });

  const del = useMutation({
    mutationFn: (file_id) => api.delete(`/backup/${file_id}`),
    onSuccess: () => { toast.success("Backup deleted"); qc.invalidateQueries({ queryKey: ["backup-list"] }); },
    onError: (e) => toast.error(formatErr(e.response?.data?.detail)),
  });

  const askRestore = (b) => {
    if (!window.confirm(
      `Restore from "${b.name}"?\n\nThis REPLACES all current data (products, sales, clients, everything) ` +
      `with the contents of this backup. This cannot be undone.\n\nTip: take a fresh backup first.`
    )) return;
    if (!window.confirm("Are you absolutely sure? All current data will be overwritten.")) return;
    restore.mutate(b.id);
  };

  const copyRedirect = () => {
    navigator.clipboard?.writeText(status?.redirect_uri || "");
    toast.success("Redirect URI copied");
  };

  return (
    <div className="space-y-6" data-testid="backups-page">
      <div>
        <div className="label-cap">Data Safety</div>
        <h1 className="font-heading text-3xl mt-1">Backups</h1>
        <p className="text-sm text-agri-ink-2 mt-1">
          Back up your whole database to your own Google Drive, and restore it here whenever you need.
        </p>
      </div>

      {status && !status.tools_available && (
        <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-agri-amber-dark">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <div>Database tools aren’t installed on this server, so backup/restore is disabled. This works
            automatically on the deployed Docker server; it’s only missing in a local dev run.</div>
        </div>
      )}

      {/* Connection card */}
      <div className="card-soft p-6">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-xl flex items-center gap-2">
            <ShieldCheck size={18} className="text-agri-forest" /> Google Drive
          </h3>
          {connected ? (
            <span className="inline-flex items-center gap-1.5 text-sm text-agri-forest font-medium">
              <CheckCircle2 size={16} /> Connected
            </span>
          ) : (
            <span className="text-sm text-agri-ink-2">Not connected</span>
          )}
        </div>

        {connected ? (
          <div className="mt-4 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
              <div><div className="label-cap">Account</div><div className="mt-1 font-medium break-all">{status.account_email || "—"}</div></div>
              <div><div className="label-cap">Connected</div><div className="mt-1">{fmtDate(status.connected_at)}</div></div>
              <div><div className="label-cap">Last backup</div><div className="mt-1">{fmtDate(status.last_backup_at)}</div></div>
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              <button data-testid="run-backup-btn" onClick={() => runBackup.mutate()} disabled={runBackup.isPending || !status.tools_available}
                className={btnPrimary + " flex items-center gap-2"}>
                <CloudUpload size={16} /> {runBackup.isPending ? "Backing up…" : "Back up now"}
              </button>
              <button onClick={() => qc.invalidateQueries({ queryKey: ["backup-list"] })} className={btnOutline + " flex items-center gap-2"}>
                <RefreshCw size={15} className={listLoading ? "animate-spin" : ""} /> Refresh
              </button>
              <button onClick={() => window.confirm("Disconnect Google Drive? Existing backups stay in your Drive.") && disconnect.mutate()}
                className={btnOutline + " flex items-center gap-2 !text-agri-danger"}>
                <Unlink size={15} /> Disconnect
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            <p className="text-sm text-agri-ink-2">
              One-time setup: create a Google OAuth credential, paste it below, then connect your account.
              See the step-by-step guide underneath.
            </p>
            <div className="grid grid-cols-1 gap-3">
              <div>
                <label className={labelCls}>Google Client ID</label>
                <input data-testid="gdrive-client-id" value={creds.client_id}
                  onChange={(e) => setCreds({ ...creds, client_id: e.target.value })}
                  placeholder="xxxxxxxx.apps.googleusercontent.com" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Google Client Secret</label>
                <input data-testid="gdrive-client-secret" value={creds.client_secret}
                  onChange={(e) => setCreds({ ...creds, client_secret: e.target.value })}
                  placeholder="GOCSPX-…" className={inputCls} />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => saveCfg.mutate()} disabled={saveCfg.isPending || creds.client_id.length < 10 || creds.client_secret.length < 5}
                className={btnOutline}>
                {saveCfg.isPending ? "Saving…" : "Save credentials"}
              </button>
              <button data-testid="connect-google-btn" onClick={() => connect.mutate()} disabled={connect.isPending || !status?.configured}
                className={btnAccent + " flex items-center gap-2"}>
                <Link2 size={16} /> Connect Google account
              </button>
            </div>
            {!status?.configured && (
              <p className="text-xs text-agri-ink-2">Save your credentials first to enable the Connect button.</p>
            )}
          </div>
        )}
      </div>

      {/* Storage gauge */}
      {connected && quota && <StorageGauge quota={quota} />}

      {/* Backups list */}
      {connected && (
        <div className="card-soft">
          <div className="p-5 border-b border-[rgba(28,28,30,0.06)]">
            <h3 className="font-heading text-xl">Backups in Drive</h3>
            <p className="text-xs text-agri-ink-2 mt-0.5">Stored in the “ZaaM Agro Backups” folder of your Google Drive.</p>
          </div>
          <TableCard className="!shadow-none">
            <thead className={theadCls}>
              <tr>
                <th className={thCls}>Backup</th>
                <th className={thCls}>Date</th>
                <th className={thCls}>Size</th>
                <th className={thCls + " text-right"}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {backups.length === 0 ? (
                <EmptyRow colSpan={4}>{listLoading ? "Loading…" : "No backups yet — click “Back up now”."}</EmptyRow>
              ) : (
                backups.map((b) => (
                  <tr key={b.id} className={rowCls}>
                    <td className={`${tdCls} rt-primary font-medium break-all`}>{b.name}</td>
                    <td className={tdCls} data-label="Date">{fmtDate(b.createdTime)}</td>
                    <td className={tdCls} data-label="Size">{fmtBytes(b.size)}</td>
                    <td className={`${tdCls} text-right rt-actions`} data-label="Actions">
                      <div className="inline-flex gap-1.5">
                        <button onClick={() => askRestore(b)} disabled={restore.isPending || !status.tools_available}
                          title="Restore this backup"
                          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-[rgba(28,28,30,0.12)] hover:bg-agri-forest/5 text-agri-forest">
                          <RotateCcw size={13} /> Restore
                        </button>
                        <button onClick={() => window.confirm(`Delete backup "${b.name}" from Drive?`) && del.mutate(b.id)}
                          title="Delete from Drive"
                          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-[rgba(28,28,30,0.12)] hover:bg-red-50 text-agri-danger">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </TableCard>
        </div>
      )}

      {/* Setup guide */}
      <div className="card-soft">
        <button onClick={() => setGuideOpen((v) => !v)} className="w-full p-5 flex items-center justify-between text-left">
          <h3 className="font-heading text-xl">First-time setup guide</h3>
          <ChevronDown size={18} className={`transition-transform ${guideOpen ? "rotate-180" : ""}`} />
        </button>
        {guideOpen && (
          <div className="px-5 pb-5 text-sm text-agri-ink-2 space-y-3">
            <p className="rounded-md bg-agri-forest/5 p-3">
              You only do this once. It creates a free Google credential so this app can upload backups to
              <b> your own</b> Drive. The app only ever sees the backup files it creates.
            </p>
            <ol className="list-decimal ml-5 space-y-2">
              <li>Go to <b>console.cloud.google.com</b> → create a project (any name).</li>
              <li><b>APIs &amp; Services → Library</b> → search “<b>Google Drive API</b>” → <b>Enable</b>.</li>
              <li><b>APIs &amp; Services → OAuth consent screen</b> → User type <b>External</b> → fill app name + your email.
                Add your own email as a <b>Test user</b>. Then, to avoid the token expiring weekly, click
                <b> Publish app</b> (status “In production” — the “unverified” warning is fine for your own use).</li>
              <li><b>APIs &amp; Services → Credentials → Create credentials → OAuth client ID</b> → type
                <b> Web application</b>.</li>
              <li>Under <b>Authorized redirect URIs</b> add exactly this:
                <div className="mt-1 flex items-center gap-2">
                  <code className="flex-1 break-all rounded bg-agri-ink/5 px-2 py-1 text-agri-ink text-xs">{status?.redirect_uri || "…"}</code>
                  <button onClick={copyRedirect} className="p-1.5 rounded-md border border-[rgba(28,28,30,0.12)] hover:bg-black/5" title="Copy"><Copy size={13} /></button>
                </div>
                <span className="text-xs">(This is your live server address. If you add a domain later, come back and add that URL too.)</span>
              </li>
              <li>Click <b>Create</b> → copy the <b>Client ID</b> and <b>Client secret</b> → paste them above →
                <b> Save credentials</b> → <b>Connect Google account</b>.</li>
            </ol>
          </div>
        )}
      </div>
    </div>
  );
}
